import { Plan, CreatePlanRequest, UpdatePlanRequest, PlanFilters } from '../types/plan.types';
import { getInstance } from '../../../auth/authWrapper';
import cfg from '../../../../config';

class PlanService {
  private baseUrl = `${cfg.apiEndpoint}/beautyAutopilot`;

  // Получить заголовки для API запросов
  private async getHeaders(): Promise<HeadersInit> {
    const headers: HeadersInit = {
      'Content-Type': 'application/json',
    };
    
    try {
      const auth = getInstance();
      const token = await auth.getTokenSilently();
      // Токен авторизации получен
      if (token) {
        headers['Authorization'] = `Bearer ${token}`;
        // Заголовок авторизации установлен
      } else {
        // Токен авторизации недоступен
      }
    } catch (error) {
      // Ошибка получения токена авторизации
    }
    return headers;
  }

  // Получить список планов
  async getPlans(filters: PlanFilters = {}, projectId: string): Promise<Plan[]> {
    try {
      const queryParams = new URLSearchParams();
      queryParams.append('projectId', projectId);
      
      if (filters.search) {
        queryParams.append('search', filters.search);
      }
      if (filters.status) {
        queryParams.append('status', filters.status);
      }
      if (filters.branchIds && filters.branchIds.length > 0) {
        filters.branchIds.forEach(id => queryParams.append('branchIds', id));
      }
      if (filters.channelIds && filters.channelIds.length > 0) {
        filters.channelIds.forEach(id => queryParams.append('channelIds', id));
      }
      // Убираем пагинацию - загружаем все планы

      const response = await fetch(`${this.baseUrl}/plans?${queryParams.toString()}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch plans');
      
      const result = await response.json();
      return result.data || result; // Возвращаем массив планов из поля data
    } catch (error) {
      // Ошибка получения планов
      throw error;
    }
  }

  // Получить план по ID
  async getPlan(id: string, projectId: string): Promise<Plan> {
    try {
      const response = await fetch(`${this.baseUrl}/plans/${id}?projectId=${projectId}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch plan');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения плана
      throw error;
    }
  }

  // Создать план
  async createPlan(planData: CreatePlanRequest, projectId: string): Promise<Plan> {
    try {
      const response = await fetch(`${this.baseUrl}/plans?projectId=${projectId}`, {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify(planData)
      });
      if (!response.ok) throw new Error('Failed to create plan');
      
      return await response.json();
    } catch (error) {
      // Ошибка создания плана
      throw error;
    }
  }

  // Обновить план
  async updatePlan(id: string, planData: UpdatePlanRequest, projectId: string): Promise<Plan> {
    try {
      const headers = await this.getHeaders();
      const url = `${this.baseUrl}/plans/${id}?projectId=${projectId}`;

      const response = await fetch(url, {
        method: 'PUT',
        headers,
        body: JSON.stringify(planData)
      });

      
      if (!response.ok) {
        const errorText = await response.text();
        // Ошибка ответа обновления плана
        throw new Error(`Failed to update plan: ${response.status} ${response.statusText}`);
      }
      
      return await response.json();
    } catch (error) {
      // Ошибка обновления плана
      throw error;
    }
  }

  // Удалить план
  async deletePlan(id: string, projectId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/plans/${id}?projectId=${projectId}`, {
        method: 'DELETE',
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete plan');
    } catch (error) {
      // Ошибка удаления плана
      throw error;
    }
  }

  // Получить доступные задачи
  async getAvailableTasks(projectId: string): Promise<any[]> {
    try {
      const response = await fetch(`${this.baseUrl}/tasks?projectId=${projectId}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch tasks');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения задач
      throw error;
    }
  }


  // Получить доступных пользователей
  async getAvailableUsers(projectId: string): Promise<any[]> {
    try {
      const response = await fetch(`${this.baseUrl}/users?projectId=${projectId}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch users');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения пользователей
      throw error;
    }
  }

  // Получить доступных CRM пользователей
  async getAvailableCrmUsers(projectId: string): Promise<any[]> {
    try {
      const response = await fetch(`${this.baseUrl}/crm-users?projectId=${projectId}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch CRM users');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения CRM пользователей
      throw error;
    }
  }

  // Получить user mappings для проекта или конкретного плана
  async getUserMappings(
    projectId: string,
    planId?: string
  ): Promise<Array<{ dhUser: { id: string; name?: string }; crmUser: { id: string; name?: string } }>> {
    try {
      const url = planId
        ? `${this.baseUrl}/user-mappings?projectId=${projectId}&planId=${planId}`
        : `${this.baseUrl}/user-mappings?projectId=${projectId}`

      const response = await fetch(url, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch user mappings');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения user mappings
      throw error;
    }
  }

  // Создать задачу
  async createTask(taskData: any, projectId: string): Promise<any> {
    try {
      const response = await fetch(`${this.baseUrl}/tasks?projectId=${projectId}`, {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify(taskData)
      });
      if (!response.ok) throw new Error('Failed to create task');
      
      return await response.json();
    } catch (error) {
      // Ошибка создания задачи
      throw error;
    }
  }

  // Удалить задачу
  async deleteTask(taskId: string, projectId: string): Promise<void> {
    try {
      const response = await fetch(`${this.baseUrl}/tasks/${taskId}?projectId=${projectId}`, {
        method: 'DELETE',
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to delete task');
    } catch (error) {
      // Ошибка удаления задачи
      throw error;
    }
  }

  // Автозаполнение данных смены
  async autofillShiftData(
    projectId: string,
    shiftDate: string,
    administratorId: string,
    taskId: string,
    params: {
      taskNumber?: number;
      variantNumber?: number;
      visitedServices?: string[];
      bookedServices?: string[];
      recordServiceList?: string[];
    },
    planId?: string
  ): Promise<Record<string, number>> {
    try {
      const response = await fetch(`${this.baseUrl}/autofill-shift-data?projectId=${projectId}`, {
        method: 'POST',
        headers: await this.getHeaders(),
        body: JSON.stringify({
          shiftDate,
          administratorId,
          taskId,
          params,
          planId
        })
      });
      if (!response.ok) {
        const errorText = await response.text();
        throw new Error(`Failed to autofill shift data: ${errorText}`);
      }
      const result = await response.json();
      return result.metrics || {};
    } catch (error) {
      // Ошибка автозаполнения
      throw error;
    }
  }

  // Получить список услуг для проекта
  // Использует эндпоинт из routes/GCB2.ts:546 /list/service
  // cfg.apiEndpoint уже содержит /api, поэтому путь: /GCB2/list/service
  async getServiceList(projectId: string): Promise<Array<{ id: string; name: string; category?: string }>> {
    try {
      const response = await fetch(`${cfg.apiEndpoint}/GCB2/list/service?projectId=${projectId}`, {
        headers: await this.getHeaders()
      });
      if (!response.ok) throw new Error('Failed to fetch service list');
      
      return await response.json();
    } catch (error) {
      // Ошибка получения списка услуг
      throw error;
    }
  }
}

export const planService = new PlanService();
